import { createEntityAdapter, Dictionary, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { get as _get } from 'lodash';

import { AddVehicleCoverage } from '@amfam/policy/add-vehicle/data-access';
import { Control, CoverageOptionModelWithControl } from '@amfam/policy/add-vehicle/ui';

import { VehicleInfo } from '../models';
import * as VehicleInfoActions from './add-vehicle-feature.actions';

export const ADD_VEHICLE_FEATURE_KEY = 'add vehicle feature';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface State extends EntityState<VehicleInfo> {
  coverages?: CoverageOptionModelWithControl[];
}

export const adapter: EntityAdapter<VehicleInfo> = createEntityAdapter<VehicleInfo>({
  selectId: state => _get(state, 'primaryVehicleInfo.vin')
});

export const initialState: State = adapter.getInitialState({});

const userReducer = createReducer(
  initialState,
  on(VehicleInfoActions.setVehicleYearVinOdo, (_state, payload) =>
    adapter.upsertOne(
      {
        primaryVehicleInfo: {
          vin: payload.vehicleInfo.vin,
          odoMeter: payload.vehicleInfo.odoMeter,
          year: payload.vehicleInfo.year
        }
      },
      initialState
    )
  ),

  on(VehicleInfoActions.setNewVehicleDiscount, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      isNewVehicleDiscountActive: payload.isNewVehicleDiscountActive
    });

    return adapter.upsertOne(updatedVehicleInfo, state);
  }),
  on(VehicleInfoActions.setVehicleUsageInfo, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      primaryUsageInfo: payload.vehicleUsage
    });

    return adapter.upsertOne(updatedVehicleInfo, state);
  }),
  on(VehicleInfoActions.setGarageInfo, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      garageAddress: payload.garageAddress
    });

    return adapter.upsertOne(updatedVehicleInfo, state);
  }),
  on(VehicleInfoActions.setVehicleFeaturesInfo, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      vehicleFeatures: payload.featureInfo
    });

    return adapter.upsertOne(updatedVehicleInfo, state);
  }),
  on(VehicleInfoActions.setVehicleUnderwritingQuestionsInfo, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      underwritingQuestions: payload.underwritingQuestions
    });

    return adapter.upsertOne(updatedVehicleInfo, state);
  }),
  on(VehicleInfoActions.setVehicleTPIsInfo, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      tpiInfo: payload.tpisInfo
    });

    return adapter.upsertOne(updatedVehicleInfo, state);
  }),
  on(VehicleInfoActions.setVehicleLeaseStartDate, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      leaseStartDate: payload.leaseStartDate
    });

    return adapter.upsertOne(updatedVehicleInfo, state);
  }),
  on(VehicleInfoActions.setVehiclePrimaryDriversInfo, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      driverAssignment: payload.driversInfo
    });

    return adapter.upsertOne(updatedVehicleInfo, state);
  }),
  on(VehicleInfoActions.setVehicleSecondaryDriversInfo, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      driverAssignment: [...state.entities[state.ids[0]].driverAssignment, ...payload.driversInfo]
    });

    return adapter.upsertOne(updatedVehicleInfo, state);
  }),
  on(VehicleInfoActions.setVehicleCoveragesInfo, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      vehicleCoverages: payload.vehicleCoverages
    });

    return adapter.upsertOne(updatedVehicleInfo, state);
  }),
  on(VehicleInfoActions.addVehicleSendNotification, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      typeOfAgentEmail: payload.payload.typeOfEmail
    });

    return adapter.upsertOne(updatedVehicleInfo, state);
  }),
  on(VehicleInfoActions.addVehicleBindQuote, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      typeOfAgentEmail: payload.payload.typeOfEmail,
      selectedAgent: payload.payload.agent,
      workOrderNumber: payload.payload.workOrderNumber
    });
    return adapter.upsertOne(updatedVehicleInfo, state);
  }),

  on(VehicleInfoActions.addVehicleSendPartnerNotification, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      typeOfAgentEmail: payload.payload.typeOfEmail
    });

    return adapter.upsertOne(updatedVehicleInfo, state);
  }),
  on(VehicleInfoActions.addVehicleBindPartnerQuote, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      typeOfAgentEmail: payload.payload.typeOfEmail,
      workOrderNumber: payload.payload.workOrderNumber
    });
    return adapter.upsertOne(updatedVehicleInfo, state);
  }),

  on(VehicleInfoActions.addVehicleExitFlow, (state, _payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      exitFlow: true
    });
    return adapter.upsertOne(updatedVehicleInfo, state);
  }),
  on(VehicleInfoActions.addVehicleReset, _state => adapter.getInitialState()),
  on(VehicleInfoActions.updateCoverage, (state, action) => {
    const optOut = action.optOutCoverages;
    const controls = action.coverage.terms.map(control => {
      if (control.options.find(o => o.name === action.value)) {
        return {
          ...control,
          selectedOption: action.value
        };
      }
      return control;
    });

    const coverages = [
      ...state.coverages.map(c => {
        if (
          optOut.includes(action.coverage.coverageCode) &&
          c.coverageCode === 'PAEmergencyRoadServiceCov_af' &&
          action.value.toLowerCase() === 'not selected'
        ) {
          return {
            ...c,
            controls: c.terms.map(con => ({ ...con, selectedOption: false }))
          };
        }
        if (c.coverageCode === action.coverage.coverageCode) {
          return {
            ...c,
            controls
          };
        }
        return c;
      })
    ].sort((x, y) => (x.isCheckbox === y.isCheckbox ? 0 : x.isCheckbox ? 1 : -1));

    return {
      ...state,
      coverages
    };
  }),
  on(VehicleInfoActions.buildPartnerCoverages, (state, action) => {
    const coverages = action.payload.result.vehicleCoverages
      .map((coverage): CoverageOptionModelWithControl => {
        const cov = coverage.terms ? coverage.terms : [];
        const coverageOptions = cov.map(term => ({
          options: term.options.map(option => option.name),
          selectedValue: coverage.selected ? term.selectedOption : ''
        }));
        return {
          isDisabled:
            action.hasCostcoGoldStarMembership && action.stateCode.toLowerCase() === 'ma'
              ? true
              : false,
          coverageCode: coverage.coverageCode,
          coverageDescription: coverage.coverageName,
          coverageSubHeading: action.copy.coverageMap[`${coverage.coverageCode}`]?.subHeading || '',
          kydTPMPackageCoverageInfo: action.hasTPMPackageAvailibility
            ? action?.copy?.coverageMap[`${coverage.coverageCode}`]?.kydPackageDefaultSelectedoption
            : '',
          options: coverageOptions[0]?.options || [],
          selectedValue: isCheckbox(coverage)
            ? coverage.selected
            : coverageOptions[0]?.selectedValue,
          deductibleDictionary: handleUninsuredDeductibleDictionaryAssignement(
            coverage,
            coverageOptions,
            action.copy.umpd
          ),
          coverageHelpTextList: handleUnderInsuredDeductibleHelpText(
            coverage,
            action.copy.coverageMap
          ),
          isCheckbox: isCheckbox(coverage, action.copy.ers),
          terms: buildControls(coverage, action.copy.optOut, action.stateCode)
        };
      })
      .sort((x, y) => (x.isCheckbox === y.isCheckbox ? 0 : x.isCheckbox ? 1 : -1));
    return { ...state, coverages };
  }),
  on(VehicleInfoActions.addVehicleNavigateToFuturePaymentSchedule, (state, payload) => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      navigateToFuturePaymentSchedule: payload.navigateToFuturePaymentSchedule
    });

    return adapter.upsertOne(updatedVehicleInfo, state);
  }),

  on(VehicleInfoActions.addVehicleOtherExitFlow, state => {
    const updatedVehicleInfo = Object.assign({}, state.entities[state.ids[0]], {
      otherExitFlow: true
    });
    return adapter.upsertOne(updatedVehicleInfo, state);
  })
);

export function reducer(state: State | undefined, action: Action) {
  return userReducer(state, action);
}

// get the selectors
const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

// select the array of vehicle info ids
export const selectVinIds = selectIds;

// select the dictionary of vehicle info entities
export const selectvehicleInfoEntities = selectEntities;

// select the array of vehicle info's
export const selectAllVehicleInfo = selectAll;

// select the total  count
export const selectVehicleInfoListCount = selectTotal;

/**
 * @description Says whether or not a coverage should be a checkbox
 * @author @jshaw-af
 * @date 08/12/2021
 * @param {AddVehicleCoverage} coverage
 * @returns {*}  {boolean}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isCheckbox(coverage: AddVehicleCoverage, copy?: any): boolean {
  if (coverage.coverageCode === copy?.ers) {
    return true;
  } else {
    return (
      (!coverage.hasTerms && coverage.terms.length === 0) ||
      (coverage.terms.length === 1 && coverage.terms[0].options.length === 1)
    );
  }
}

function handleUninsuredDeductibleDictionaryAssignement(
  coverage: AddVehicleCoverage,
  coverageOptions: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options: any[];
    selectedValue: string;
  }[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  copy?: any
) {
  const uninsuredMotoristPropertyDamageDeductibleDictionary: Dictionary<string> = {};
  if (coverageOptions?.length > 0 && coverage.coverageCode === copy.umpd) {
    for (let i = 0; i < coverageOptions[0].options.length; i++) {
      uninsuredMotoristPropertyDamageDeductibleDictionary[coverageOptions[0].options[i]] =
        convertRejectedToNoCoverage(coverageOptions[0].options[i], coverageOptions[1].options);
    }
  }
  return uninsuredMotoristPropertyDamageDeductibleDictionary;
}

function convertRejectedToNoCoverage(umpdCoverageOption: string, deductibleOptions: string[]) {
  if (umpdCoverageOption.toLowerCase() === 'rejected') {
    return deductibleOptions.find(option => option.toLowerCase() === 'no coverage');
  }
  return deductibleOptions.find(option => option.toLowerCase() !== 'no coverage');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleUnderInsuredDeductibleHelpText(coverage: AddVehicleCoverage, copy: any) {
  const underInsuredHelpTextObj: string[] = [];
  if (coverage.terms) {
    for (const term of coverage.terms) {
      if (term.name === copy.hit) {
        const str = copy[`${coverage.coverageCode}`]['coverageHelpText1']?.replace(
          '$${hitdeductible}',
          term.selectedOption
        );
        underInsuredHelpTextObj.push(str);
      } else if (term.name === copy.notAhit) {
        const str = copy[`${coverage.coverageCode}`]['coverageHelpText2']?.replace(
          '$${noHitdeductible}',
          term.selectedOption
        );
        underInsuredHelpTextObj.push(str);
      }
    }
  }
  return underInsuredHelpTextObj;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildControls(coverage: AddVehicleCoverage, optOut: any, _stateCode: string): Control[] {
  if (isCheckbox(coverage)) {
    return [
      {
        name: coverage.coverageName,
        chosenTermValue: coverage?.selected,
        options: []
      }
    ];
  }

  return buildControlsDefaults(coverage, optOut);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildControlsDefaults(coverage: AddVehicleCoverage, optOut: any): Control[] {
  const canOptOut = optOut.includes(coverage.coverageCode);

  return coverage.terms.map(t => ({
    name: t.name,
    selectedOption: t?.selectedOption,
    options: canOptOut
      ? [...t.options.map(o => ({ name: o.name })), { name: 'Not Selected' }]
      : t.options.map(o => ({ name: o.name }))
  }));
}
